import React, { useMemo } from 'react'
import type { PropsWithChildren } from 'react'
import Faye from 'faye'

type Publication = <T>(channel: string, message: T) => Promise<void>

interface Subscription<T> extends Promise<void> {
  cancel: () => void
  withChannel: (cb: (channel: string, message: T) => void) => void
}

export interface SimplePubSubClient {
  publish: Publication
  subscribe: <T>(channel: string, cb?: (message: T) => void) => Subscription<T>
}

const SimplePubSubClientContext =
  React.createContext<SimplePubSubClient | null>(null)

interface SimplePubSubClientProviderProps {
  url: string
}

export const SimplePubSubClientProvider = ({
  url,
  children,
}: PropsWithChildren<SimplePubSubClientProviderProps>) => {
  const client = useMemo(
    () => new Faye.Client(url) as SimplePubSubClient,
    [url]
  )

  return (
    <SimplePubSubClientContext.Provider value={client}>
      {children}
    </SimplePubSubClientContext.Provider>
  )
}

export const useSimplePubSubClient = () => {
  const context = React.useContext(SimplePubSubClientContext)

  if (context === null) {
    throw new Error(
      'useSimplePubSubClient must be used within a SimplePubSubClientProvider component'
    )
  }

  return context
}
